"use client";

import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import clsx from "clsx";

import type { StrapiSlideInfo, StrapiSliderInfo } from "@/shared/api/strapi";
import { useHorizontalSlider } from "@/shared/lib/slider/useHorizontalSlider";
import { StrapiImage } from "@/shared/ui/strapi-image";

import styles from "./InfoSlider.module.css";

type VariantSlider = StrapiSliderInfo["variant"];

export interface InfoSliderProps {
  initialSlide?: number;
  showNavButtons?: boolean;
  slides: StrapiSlideInfo[];
  className?: string;
  variant?: VariantSlider;
}

const variantMap = {
  SECONDARY: styles.secondary,
  CYAN: styles.cyan,
} as const satisfies Record<VariantSlider, string>;

export function InfoSlider({
  initialSlide = 0,
  showNavButtons = true,
  slides,
  className,
  variant = "SECONDARY",
}: InfoSliderProps) {
  const slidesData = slides || [];
  const totalSlides = slidesData.length;
  const { currentSlide, sliderRef, goToSlide } = useHorizontalSlider(
    totalSlides,
    initialSlide,
  );

  if (totalSlides === 0) {
    return null;
  }

  return (
    <div
      className={clsx(styles.sliderWrapper, className, variantMap[variant])}
    >
      {showNavButtons && (
        <div className={styles.navButtons} role="tablist">
          {slidesData.map((slide, index) => (
            <button
              key={slide.label.name || index}
              type="button"
              className={clsx(
                styles.navBtn,
                currentSlide === index && styles.active,
              )}
              onClick={() => goToSlide(index)}
              aria-label={`Перейти к слайду ${slide.label.name}`}
              aria-controls={`slide-${index}`}
              role="tab"
              aria-selected={currentSlide === index}
            >
              {slide.label.name}
            </button>
          ))}
        </div>
      )}

      <div className={styles.sliderBox}>
        <div
          className={styles.sliderContainer}
          ref={sliderRef}
          role="region"
          aria-label="Слайдер с изображениями"
        >
          <div className={styles.slides}>
            {slidesData.map((slide, index) => (
              <div
                key={slide.label.name || index}
                id={`slide-${index}`}
                className={styles.slide}
                role="tabpanel"
                aria-label={`Слайд ${slide.label.name}`}
                aria-hidden={currentSlide !== index}
              >
                <StrapiImage
                  image={slide.content.image}
                  width={900}
                  height={573}
                  className={styles.slideImage}
                />
                <div className={styles.slideContent}>
                  <BlocksRenderer content={slide.content.content} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
